import React from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { Box, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { capitalize } from '../../../../utils/functions'
import { tableStyles, ui, tone } from '../../../../utils/uiTokens'
import { useHeadScope } from '../../../head/headScope'
import { studentControlApi } from '../../../../features/curator/studentControl'
import { formatDay } from '../../studentProgressPage/StudentControlBlock'

interface Props {
    courseId?: number
    groupId?: number
}

const chip = (t: { text: string; bg: string; border: string }) => ({
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '24px',
    fontSize: '12.5px',
    fontWeight: 600,
    color: t.text,
    backgroundColor: t.bg,
    border: `1px solid ${t.border}`,
    whiteSpace: 'nowrap' as const,
})

// Шыққандар и возвраты из CRM: серым, без телефонов
const WithdrawnTable: React.FC<Props> = ({ courseId, groupId }) => {
    const { formatMessage, locale } = useIntl()
    const { basePath } = useHeadScope()
    const { data = [], isFetching } = studentControlApi.useFetchWithdrawnQuery({ courseId }, { skip: !courseId })
    const rows = groupId ? data.filter((r) => r.groupId === groupId) : data

    if (isFetching) return <LinearProgress />

    return (
        <TableContainer sx={{ ...tableStyles.container, overflowX: 'auto' }}>
            <Table size="small">
                <TableHead sx={tableStyles.head}>
                    <TableRow>
                        <TableCell>{formatMessage({ id: 'fullName' })}</TableCell>
                        <TableCell>{formatMessage({ id: 'group' })}</TableCell>
                        <TableCell>{formatMessage({ id: 'subject' })}</TableCell>
                        <TableCell>{formatMessage({ id: 'withdrawnDate' })}</TableCell>
                        <TableCell>{formatMessage({ id: 'reason' })}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length ? (
                        rows.map((r) => (
                            <TableRow
                                key={`${r.userSubjectEnrollId}-${r.kind}`}
                                sx={{ ...tableStyles.row, '& .MuiTableCell-root': { ...tableStyles.row['& .MuiTableCell-root'], color: ui.muted } }}
                            >
                                <TableCell>
                                    <Link
                                        to={`${basePath}/students/${r.userCourseEnrollId}?fullName=${r.firstName}-${r.lastName}`}
                                        style={{ color: ui.sub, fontWeight: 600, textDecoration: 'none' }}
                                    >
                                        {r.firstName} {r.lastName}
                                    </Link>
                                </TableCell>
                                <TableCell>{r.groupTitle}</TableCell>
                                <TableCell>{r[`subjectTitle${capitalize(locale)}`] || r.subjectTitleKz}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDay(r.withdrawnAt)}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                                        <Box component="span" sx={chip(r.kind === 'RETURNED' ? tone.warn : tone.neutral)}>
                                            {formatMessage({ id: r.kind === 'RETURNED' ? 'withdrawnReturned' : 'withdrawnMark' })}
                                        </Box>
                                        {r.withdrawReason && formatMessage({ id: `withdrawReason${r.withdrawReason}`, defaultMessage: r.withdrawReason })}
                                        {r.note && <Box component="span" sx={{ color: ui.muted }}>· {r.note}</Box>}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ color: ui.muted, py: 4 }}>
                                {formatMessage({ id: 'withdrawnEmpty' })}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default WithdrawnTable
